import {assert} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';

import {processTimingEvent} from './BenchlingProfiler';
import {toDatadogTagFormat} from './PerformanceUtils';
import {ProfilerEvent, ProfilerEventDomain, EventMetadata} from './ProfilerConstants';

/* Should bump version anytime there's a change to logging events or processing them,
 * so that on Datadog, we can quickly see which custom actions have outdated logic or tags
 */
const PROFILER_PROCESS_EVENTS_VERSION = 1;

export enum ProfilerProcessesFeatures {
  CREATE_TASK_GROUP = 'createTaskGroup',
  EXECUTE_INTO_ENTRY = 'executeIntoEntry',
  DIRECT_EXECUTE = 'directExecute',
  SUBMIT_TASK_TABLE = 'submitTaskTable',
}

const FEATURE_TO_PROCESSING_FUNCTION: {[key in ProfilerProcessesFeatures]: Function} = {
  [ProfilerProcessesFeatures.CREATE_TASK_GROUP]: processCreateProcessAndTasks,
  [ProfilerProcessesFeatures.EXECUTE_INTO_ENTRY]: processExecuteIntoEntry,
  [ProfilerProcessesFeatures.DIRECT_EXECUTE]: processDirectExecute,
  [ProfilerProcessesFeatures.SUBMIT_TASK_TABLE]: processSubmitTaskTable,
};

enum ProfilerProcessesActions {
  CREATE_TASK_GROUP_STARTED = 'createTaskGroupStarted',
  CREATE_TASK_GROUP_ENDED = 'createTaskGroupEnded',

  EXECUTE_INTO_ENTRY_STARTED = 'executeIntoEntryStarted',
  EXECUTE_INTO_ENTRY_ENDED = 'executeIntoEntryEnded',

  DIRECT_EXECUTE_STARTED = 'directExecuteStarted',
  DIRECT_EXECUTE_ENDED = 'directExecuteEnded',

  SUBMIT_TASK_TABLE_STARTED = 'submitTaskTableStarted',
  SUBMIT_TASK_TABLE_ENDED = 'submitTaskTableEnded',
}

export const PROCESSES_CREATE_TASK_GROUP_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.CREATE_TASK_GROUP,
  action: ProfilerProcessesActions.CREATE_TASK_GROUP_STARTED,
};

export const PROCESSES_CREATE_TASK_GROUP_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.CREATE_TASK_GROUP,
  action: ProfilerProcessesActions.CREATE_TASK_GROUP_ENDED,
};

export const PROCESSES_EXECUTE_INTO_ENTRY_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.EXECUTE_INTO_ENTRY,
  action: ProfilerProcessesActions.EXECUTE_INTO_ENTRY_STARTED,
};

export const PROCESSES_EXECUTE_INTO_ENTRY_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.EXECUTE_INTO_ENTRY,
  action: ProfilerProcessesActions.EXECUTE_INTO_ENTRY_ENDED,
};

export const PROCESSES_DIRECT_EXECUTE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.DIRECT_EXECUTE,
  action: ProfilerProcessesActions.DIRECT_EXECUTE_STARTED,
};

export const PROCESSES_DIRECT_EXECUTE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.DIRECT_EXECUTE,
  action: ProfilerProcessesActions.DIRECT_EXECUTE_ENDED,
};

export const PROCESSES_SUBMIT_TASK_TABLE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.SUBMIT_TASK_TABLE,
  action: ProfilerProcessesActions.SUBMIT_TASK_TABLE_STARTED,
};

export const PROCESSES_SUBMIT_TASK_TABLE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.PROCESSES,
  feature: ProfilerProcessesFeatures.SUBMIT_TASK_TABLE,
  action: ProfilerProcessesActions.SUBMIT_TASK_TABLE_ENDED,
};

export function processProcessesEvents(profilerEvent: Pick<ProfilerEvent, 'feature' | 'action'>): void {
  const feature = profilerEvent.feature as ProfilerProcessesFeatures;
  assert(
    Object.values(ProfilerProcessesFeatures).includes(feature),
    `Unexpected profiler feature: ${profilerEvent.feature}`
  );
  FEATURE_TO_PROCESSING_FUNCTION[feature]();
}

export function getSizeBucket(num: number): string {
  if (num === 0) {
    return 'eq_0';
  } else if (num <= 100) {
    return '1_to_100';
  } else if (num <= 500) {
    return '100_to_500';
  } else if (num <= 1000) {
    return '500_to_1000';
  } else {
    return 'gt_1000';
  }
}

interface ProcessEventMetadataOptions {
  numItems: number;
  actionOrigin?: ActionOrigin;
  tableLocation?: string;
  numTasks?: number;
  numOutputs?: number;
}

export function createProcessEventMetadata({
  numItems,
  actionOrigin,
  tableLocation,
  numTasks,
  numOutputs,
}: ProcessEventMetadataOptions): EventMetadata {
  // NOTE: All tag keys and values MUST be snake case, alphanumeric and lowercase with no special characters
  // other than hyphens and underscores. This is to match Datadog's tag format expectations
  const metadata: EventMetadata = {
    distributionMetadata: {
      size_bucket: getSizeBucket(numItems),
    },
    actionMetadata: {
      size_bucket: getSizeBucket(numItems),
      num_items: numItems,
    },
  };

  if (numTasks != null) {
    metadata.actionMetadata.num_tasks = numTasks;
  }

  if (numOutputs != null) {
    metadata.actionMetadata.num_outputs = numOutputs;
  }

  if (actionOrigin != null) {
    // For perf purposes we can consider any action origin from the sidepanel (search dashboard)
    // to be the same
    let coercedActionOrigin: string = actionOrigin;
    if (
      actionOrigin === ActionOrigin.SIDEPANEL_GLOBAL_CREATE ||
      actionOrigin === ActionOrigin.WORKFLOWS_SIDEPANEL_CREATE
    ) {
      coercedActionOrigin = 'SIDEPANEL';
    }

    coercedActionOrigin = toDatadogTagFormat(coercedActionOrigin);

    metadata.distributionMetadata.action_origin = coercedActionOrigin;
    metadata.actionMetadata.action_origin = coercedActionOrigin;
  }
  if (tableLocation != null) {
    const coercedTableLocation = toDatadogTagFormat(tableLocation);
    metadata.distributionMetadata.table_location = coercedTableLocation;
    metadata.actionMetadata.table_location = coercedTableLocation;
  }

  return metadata;
}

/**
 * Process a processes (workflows) generate analysis event
 */
function processCreateProcessAndTasks(): void {
  processTimingEvent(
    PROCESSES_CREATE_TASK_GROUP_STARTED_EVENT,
    PROCESSES_CREATE_TASK_GROUP_ENDED_EVENT,
    'Workflows Create Task Group with Tasks',
    'perf.process_create_task_group_with_tasks',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}

function processExecuteIntoEntry(): void {
  processTimingEvent(
    PROCESSES_EXECUTE_INTO_ENTRY_STARTED_EVENT,
    PROCESSES_EXECUTE_INTO_ENTRY_ENDED_EVENT,
    'Workflows Execute Into Entry',
    'perf.process_execute_into_entry',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}

function processDirectExecute(): void {
  processTimingEvent(
    PROCESSES_DIRECT_EXECUTE_STARTED_EVENT,
    PROCESSES_DIRECT_EXECUTE_ENDED_EVENT,
    'Workflows Direct Execute',
    'perf.process_direct_execute',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}

function processSubmitTaskTable(): void {
  processTimingEvent(
    PROCESSES_SUBMIT_TASK_TABLE_STARTED_EVENT,
    PROCESSES_SUBMIT_TASK_TABLE_ENDED_EVENT,
    'Workflows Submit Task Table',
    'perf.process_submit_task_table',
    PROFILER_PROCESS_EVENTS_VERSION
  );
}
